import { concat, flatten, isEmpty, some, values } from 'lodash';
import moment from 'moment';
import type MeetingContentDto from '../../../../../types/MeetingContentDto';
import type MeetingDto from '../../../../../types/MeetingDto';
import type MeetingMetadataDto from '../../../../../types/MeetingMetadataDto';
import type MeetingNoteDto from '../../../../../types/MeetingNoteDto';
import type MeetingSpaceDto from '../../../../../types/MeetingSpaceDto';
import type MiniUser from '../../../../../types/MiniUser';
import type { CurrentUserDto } from '../../../../../types/UserDtos';

const PRIVATE = 'PRIVATE';
const SHARED = 'SHARED';

const flat = (objectOfArrays: { [key: string]: any }) => flatten(values(objectOfArrays));

export function isParticipant(meeting: Pick<MeetingDto, 'participants'>, user: Pick<MiniUser, 'id'>) {
    return some(meeting.participants, p => p.id === user.id);
}

export function getOtherParticipant(
    meeting: Pick<MeetingDto, 'participants'> | Pick<MeetingSpaceDto, 'participants'>,
    executingUser: { id: string }
): MiniUser {
    const { participants } = meeting;
    return participants.find(p => p.id !== executingUser.id)!;
}

export function isValid(meeting: Pick<MeetingDto, 'participants'>) {
    return (meeting.participants || []).length === 2;
}

export function getPrivateNotes({ notes = [] }: { notes: Pick<MeetingNoteDto, 'visibility'>[] }) {
    return notes.filter(c => c.visibility === PRIVATE);
}

export function getSharedNotes({ notes = [] }: { notes: Pick<MeetingNoteDto, 'visibility'>[] }) {
    return notes.filter(c => c.visibility === SHARED);
}

export function hasContent(meeting: Pick<MeetingDto, 'talkingPoints' | 'notes'>) {
    return !isEmpty(meeting) && (!isEmpty(meeting.talkingPoints) || !isEmpty(meeting.notes));
}

export function hasPrivateContent(meeting: Pick<MeetingDto, 'notes' | 'talkingPoints' | 'attachments'>) {
    const { notes = [], talkingPoints = {} } = meeting;
    const content = concat(notes, flat(talkingPoints));
    return some(content, c => c.visibility === PRIVATE);
}

export function hasNotes(meeting: Pick<MeetingDto, 'notes'>) {
    return !isEmpty(meeting) && !isEmpty(meeting.notes);
}

export function hasTalkingPoints(meeting: Pick<MeetingDto, 'talkingPoints'>) {
    return !isEmpty(meeting) && !isEmpty(meeting.talkingPoints);
}

export function getMeetingResourceId(participant1: Pick<MiniUser, 'id'>, participant2: Pick<MiniUser, 'id'>) {
    return `${participant1.id}${participant2.id}`;
}

export function isHidePrivate(hidePrivate: boolean, content: Pick<MeetingContentDto, 'visibility'>) {
    return hidePrivate && isPrivate(content);
}

export function isPrivate(content: Pick<MeetingContentDto, 'visibility'>) {
    return content.visibility === PRIVATE;
}

export function isHidden(content: Pick<MeetingNoteDto, 'visibility' | 'draft'>) {
    return isPrivate(content) || content.draft;
}

export function getMeetingTitle(meeting: Pick<MeetingDto, 'title' | 'calendarDate'>, currentUser: CurrentUserDto) {
    const dateFormat = currentUser.company.companySettings.dateFormats.shortDateWithWeekday;
    const dateLabel = moment(meeting.calendarDate).format(dateFormat);
    return `${dateLabel} - ${isEmpty(meeting.title) ? '1:1 Meeting' : meeting.title}`;
}

/**
 * @returns {Moment} The current date with the time (hours and minutes) from the supplied meeting
 * If no meeting is supplied (or it doesn't have a date), the time 10:00 is used as
 * a default (usually within office hours).
 */
export function getMeetingDateOrDefault(meeting: Pick<MeetingMetadataDto, 'date'> | undefined) {
    let hour = 10;
    let minute = 0;

    if (meeting && meeting.date) {
        const meetingDate = moment(meeting.date);
        hour = meetingDate.get('hours');
        minute = meetingDate.get('minutes');
    }

    return moment().hours(hour).minutes(minute).seconds(0).milliseconds(0);
}

export function toSpaceHash(participants: string[] | MiniUser[]): string {
    const userIds = participants.map(p => (typeof p === 'object' ? p.id : p));
    userIds.sort();
    return userIds.join('');
}
